import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { HiPlus, HiDotsVertical, HiTrash } from 'react-icons/hi'
import { useState, useRef, useEffect } from 'react'
import TaskCard from './TaskCard'

const STATUS_COLORS = {
  'To Do': 'bg-status-todo',
  'In Progress': 'bg-status-in-progress',
  'Review': 'bg-status-review',
  'Done': 'bg-status-done',
  'Blocked': 'bg-status-blocked',
}

export default function BoardColumn({ column, onAddTask, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  // Column is a droppable container for tasks (not sortable itself)
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      column,
    },
  })

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const statusColor = STATUS_COLORS[column.name] || 'bg-surface-400'

  return (
    <div
      ref={setNodeRef}
      className={`w-72 shrink-0 flex flex-col bg-surface-50 dark:bg-surface-800 rounded-xl max-h-full border transition-colors duration-200 ${
        isOver ? 'border-brand-400 bg-brand-50/30 ring-1 ring-brand-200' : 'border-surface-200 dark:border-surface-700'
      }`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between px-3 py-3">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${statusColor} ring-2 ring-white dark:ring-surface-800 shadow-sm`}></span>
          <h3 className="font-semibold text-surface-800 dark:text-surface-200">{column.name}</h3>
          <span className="text-xs font-medium text-surface-600 dark:text-surface-400 bg-surface-200 dark:bg-surface-700 px-2 py-0.5 rounded-full">
            {column.tasks?.length || 0}
          </span>
        </div>

        <div className="flex items-center gap-0.5">
          <button
            onClick={onAddTask}
            className="p-1.5 text-surface-400 dark:text-surface-500 hover:text-surface-600 hover:bg-surface-200 dark:hover:bg-surface-600 active:bg-surface-300 dark:active:bg-surface-500 rounded-lg transition-all duration-150"
            title="Add task"
          >
            <HiPlus className="w-4 h-4" />
          </button>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1.5 text-surface-400 dark:text-surface-500 hover:text-surface-600 hover:bg-surface-200 dark:hover:bg-surface-600 active:bg-surface-300 dark:active:bg-surface-500 rounded-lg transition-all duration-150"
            >
              <HiDotsVertical className="w-4 h-4" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-lg py-1.5 z-10 animate-fade-in">
                <button
                  onClick={() => {
                    setMenuOpen(false)
                    onDelete()
                  }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                >
                  <HiTrash className="w-4 h-4" />
                  Delete column
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tasks List — droppable area */}
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-[60px]">
        <SortableContext
          items={column.tasks?.map(t => t.id) || []}
          strategy={verticalListSortingStrategy}
        >
          {column.tasks?.map(task => (
            <TaskCard key={task.id} task={task} />
          ))}
        </SortableContext>

        {/* Empty state */}
        {(!column.tasks || column.tasks.length === 0) && (
          <div className={`text-center py-8 text-sm rounded-lg border-2 border-dashed transition-colors ${
            isOver ? 'border-brand-300 text-brand-500 bg-brand-50/50' : 'border-transparent text-surface-400 dark:text-surface-500'
          }`}>
            <p>{isOver ? 'Drop here' : 'No tasks yet'}</p>
            {!isOver && <p className="text-xs mt-1 text-surface-300 dark:text-surface-600">Drop tasks here or add new ones</p>}
          </div>
        )}
      </div>

      {/* Add Task Button */}
      <div className="px-2 pb-2">
        <button
          onClick={onAddTask}
          className="w-full py-2.5 text-sm text-surface-500 dark:text-surface-400 hover:text-surface-700 hover:bg-surface-100 dark:hover:bg-surface-700 active:bg-surface-200 dark:active:bg-surface-600 rounded-lg transition-all duration-150 flex items-center justify-center gap-1.5 border border-dashed border-surface-300 dark:border-surface-600 hover:border-surface-400 dark:hover:border-surface-500"
        >
          <HiPlus className="w-4 h-4" />
          Add task
        </button>
      </div>
    </div>
  )
}
